import { memo, useMemo, useState } from "react";
import { EditableFence } from "./types";

type FenceListPanelProps = {
  fences: EditableFence[];
  selectedId: string | null;
  unsavedCount: number;
  onSelect: (fence: EditableFence) => void;
};

const STATUS_BADGE: Record<EditableFence["status"], { label: string; className: string }> = {
  NEW: { label: "New", className: "bg-blue-100 text-blue-800" },
  EDITED: { label: "Edited", className: "bg-green-100 text-green-800" },
  ACTIVE: { label: "Saved", className: "bg-gray-100 text-gray-700" },
};

/**
 * Searchable list of every fence. Unsaved fences float to the top. Clicking a
 * row selects it and zooms the map; Save / Undo live in the Selected fence panel.
 */
const FenceListPanel = memo(function FenceListPanel({
  fences,
  selectedId,
  unsavedCount,
  onSelect,
}: FenceListPanelProps) {
  const [query, setQuery] = useState("");

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = q
      ? fences.filter((f) => f.name.toLowerCase().includes(q))
      : fences;

    // Unsaved first, then alphabetical, unnamed at the end.
    return [...matches].sort((a, b) => {
      const aDirty = a.status !== "ACTIVE" ? 0 : 1;
      const bDirty = b.status !== "ACTIVE" ? 0 : 1;
      if (aDirty !== bDirty) return aDirty - bDirty;
      if (!a.name) return 1;
      if (!b.name) return -1;
      return a.name.localeCompare(b.name);
    });
  }, [fences, query]);

  return (
    <section className="flex h-full flex-col rounded-lg border-2 border-gray-200 bg-slate-100 shadow">
      <header className="border-b border-gray-200 p-3">
        <div className="mb-2 flex items-center justify-between">
          <h5 className="text-sm font-bold text-gray-900">Geofences</h5>
          <span className="text-xs text-gray-600">
            {fences.length} total
            {unsavedCount > 0 && (
              <>
                {" · "}
                <span className="font-semibold text-amber-700">
                  {unsavedCount} unsaved
                </span>
              </>
            )}
          </span>
        </div>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search fences…"
          aria-label="Search fences"
          className="w-full rounded-md border border-gray-300 bg-white p-2 text-sm focus:border-blue-500 focus:outline-none"
        />
      </header>

      <ul className="flex-1 overflow-y-auto" role="listbox" aria-label="Geofences">
        {visible.length === 0 && (
          <li className="p-4 text-sm text-gray-500">
            {fences.length === 0 ? "No geofences yet. Draw one on the map." : "No fences match that search."}
          </li>
        )}
        {visible.map((fence) => {
          const isSelected = fence.locationId === selectedId;
          const badge = STATUS_BADGE[fence.status];

          return (
            <li
              key={fence.locationId}
              role="option"
              aria-selected={isSelected}
              onClick={() => onSelect(fence)}
              className={`cursor-pointer border-b border-gray-200 px-3 py-2 ${
                isSelected ? "bg-violet-50" : "bg-white hover:bg-gray-50"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm font-medium text-gray-900">
                  {fence.name || <span className="italic text-gray-400">Unnamed fence</span>}
                </span>
                <span className={`shrink-0 rounded px-2 py-0.5 text-xs font-semibold ${badge.className}`}>
                  {badge.label}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
});

export default FenceListPanel;
